import { useState, useEffect } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { ConfigList } from './components/ConfigList';
import { ConfigForm } from './components/ConfigForm';
import { JsonPreview } from './components/JsonPreview';
import { Login } from './components/Login';
import { AdminLTELayout } from './components/AdminLTELayout';

import { WorkloadConfig } from './types';
import { ApiService } from './services/api';
import { LocalFsApiService } from './services/local-fs-api';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { showSuccess, showError } from './utils/toast';
import './App.css';

type View = 'list' | 'create' | 'edit' | 'preview';

const USE_LOCAL_FS = import.meta.env.VITE_USE_LOCAL_FS === 'true';

function AppContent() {
  const { isAuthenticated, loading, user, signOut } = useAuth();
  const [view, setView] = useState<View>('list');
  const [selectedWorkload, setSelectedWorkload] = useState<string | undefined>();
  const [previewConfig, setPreviewConfig] = useState<WorkloadConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [directoryAccess, setDirectoryAccess] = useState(false);
  const [directoryError, setDirectoryError] = useState<string | null>(null);

  useEffect(() => {
    // Check if we're using local FS and if we have access
    if (USE_LOCAL_FS) {
      setDirectoryAccess(LocalFsApiService.hasDirectoryAccess());
    }
  }, []);

  const handleRequestDirectoryAccess = async () => {
    try {
      setDirectoryError(null);
      await LocalFsApiService.requestDirectoryAccess();
      setDirectoryAccess(true);
    } catch (error) {
      setDirectoryError(error instanceof Error ? error.message : 'Failed to access directory');
    }
  };

  const handleCreate = () => {
    setSelectedWorkload(undefined);
    setView('create');
  };

  const handleEdit = (workloadName: string) => {
    setSelectedWorkload(workloadName);
    setView('edit');
  };

  const handleSave = async (config: WorkloadConfig) => {
    // Custom validation for accountId logic
    const errors: string[] = [];
    
    config.environments?.forEach((env, index) => {
      // Treat empty string, null, and undefined as "no account ID"
      const hasAccountId = env.accountId && env.accountId.trim() !== '';
      const hasVendingFields = env.accountName || env.accountEmail || env.organizationalUnit;
      
      if (!hasAccountId && !hasVendingFields) {
        errors.push(`Environment ${index + 1} (${env.environmentType || 'unknown'}): Must provide either accountId OR account vending fields (accountName, accountEmail, organizationalUnit)`);
      }
      
      // Validate accountId format if provided
      if (hasAccountId) {
        const accountIdPattern = /^[0-9]{12}$/;
        if (!accountIdPattern.test(env.accountId!)) {
          errors.push(`Environment ${index + 1} (${env.environmentType || 'unknown'}): accountId must be exactly 12 digits`);
        }
      }
      
      // Account vending mode - require all vending fields when accountId is empty
      if (!hasAccountId) {
        if (!env.accountName || env.accountName.trim() === '') {
          errors.push(`Environment ${index + 1} (${env.environmentType || 'unknown'}): accountName is required when accountId is not provided`);
        }
        if (!env.accountEmail || env.accountEmail.trim() === '') {
          errors.push(`Environment ${index + 1} (${env.environmentType || 'unknown'}): accountEmail is required when accountId is not provided`);
        }
        if (!env.organizationalUnit || env.organizationalUnit.trim() === '') {
          errors.push(`Environment ${index + 1} (${env.environmentType || 'unknown'}): organizationalUnit is required when accountId is not provided`);
        }
      }
    });
    
    if (errors.length > 0) {
      showError(
        errors.join('\n\n'),
        'Validation Error',
        undefined,
        10000
      );
      return;
    }
    
    // Validation passed, proceed to preview
    setPreviewConfig(config);
    setView('preview');
  };

  const handleConfirmSave = async () => {
    if (!previewConfig) return;

    // Close the preview modal immediately and show loading state
    setView('list');
    setSaving(true);

    try {
      let result;
      if (selectedWorkload) {
        result = await ApiService.updateConfig(selectedWorkload, previewConfig);
      } else {
        result = await ApiService.createConfig(previewConfig);
      }

      // Show success message with MR link if available
      if (result?.mergeRequest?.url) {
        const action = selectedWorkload ? 'updated' : 'created';
        showSuccess(
          `Configuration ${action} successfully! A merge request has been created. The changes will be deployed once approved and merged.`,
          'Success',
          {
            text: 'View Merge Request',
            url: result.mergeRequest.url
          }
        );
      } else {
        showSuccess('Configuration saved successfully!');
      }

      setPreviewConfig(null);
      setSelectedWorkload(undefined);
    } catch (err: any) {
      // Handle validation errors with details
      if (err?.details && Array.isArray(err.details)) {
        const errorMessages = err.details.map((detail: any) => 
          `• ${detail.field}: ${detail.message}`
        ).join('\n');
        showError(
          `${err.error || 'Validation failed'}:\n\n${errorMessages}`,
          'Validation Error',
          undefined,
          15000 // Show for 15 seconds
        );
      } else {
        showError(
          err instanceof Error ? err.message : (err?.error || 'Unknown error occurred'),
          'Failed to Save'
        );
      }
      // Go back to form so user can fix errors
      setView(selectedWorkload ? 'edit' : 'create');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelPreview = () => {
    // Go back to the form from preview (keep previewConfig so form can restore data)
    setView(selectedWorkload ? 'edit' : 'create');
  };

  const handleCancel = () => {
    // Go back to list from form
    setView('list');
    setSelectedWorkload(undefined);
    setPreviewConfig(null);
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <Login onLoginSuccess={() => window.location.reload()} />;
  }

  const handleSignOut = async () => {
    try {
      await signOut();
      window.location.reload();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <AdminLTELayout 
      userName={user?.name || user?.email || user?.username || 'User'} 
      onSignOut={handleSignOut}
    >
      {USE_LOCAL_FS && !directoryAccess && (
        <Alert variant="info" className="mb-4">
          <Alert.Heading>Local File System Mode</Alert.Heading>
          <p>
            This UI is configured to read and write workload configurations directly from your local file system.
            Click the button below to select the <code>workloads</code> directory.
          </p>
          {directoryError && (
            <Alert variant="danger" className="mt-2">
              {directoryError}
            </Alert>
          )}
          <Button onClick={handleRequestDirectoryAccess} variant="primary">
            Select Workloads Directory
          </Button>
          <hr />
          <p className="mb-0 small">
            <strong>Note:</strong> This feature requires a modern browser with File System Access API support (Chrome/Edge 86+).
            Navigate to the root of your project and select the <code>workloads</code> folder.
          </p>
        </Alert>
      )}

      {(!USE_LOCAL_FS || directoryAccess) && (
        <>
          {saving && (
            <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9998 }}>
              <div className="bg-white p-4 rounded shadow">
                <div className="d-flex align-items-center">
                  <div className="spinner-border text-primary me-3" role="status">
                    <span className="visually-hidden">Saving...</span>
                  </div>
                  <div>
                    <h5 className="mb-1">Saving Configuration</h5>
                    <p className="mb-0 text-muted">Creating merge request...</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {view === 'list' && (
            <ConfigList 
              onEdit={handleEdit} 
              onCreate={handleCreate}
            />
          )}

          {(view === 'create' || view === 'edit') && (
            <ConfigForm
              workloadName={selectedWorkload}
              initialData={previewConfig || undefined}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          )}

          {view === 'preview' && previewConfig && (
            <JsonPreview
              config={previewConfig}
              onClose={handleCancelPreview}
              onSave={saving ? undefined : handleConfirmSave}
            />
          )}
        </>
      )}
    </AdminLTELayout>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;